import { useRef, useState, useCallback, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react'
import { Bold, Italic, Code, Link2, List, ListOrdered, Quote, ImagePlus } from 'lucide-react'
import { cn } from './cn'
import { renderMarkdown } from '../lib/markdown'

/**
 * A markdown source editor with a formatting toolbar, Write/Preview tabs, and
 * paste- or drop-to-upload.
 *
 * Deliberately a textarea rather than TipTap/ProseMirror: what gets stored is
 * markdown *source*, and a rich-text editor would mean a two-way
 * markdown↔document serialization layer — lossy in general, and with no idea
 * what `attachment:41` means in particular. A textarea also gets
 * paste-to-upload and cursor-accurate insertion for free.
 */
export type MarkdownComposerProps = {
  value: string
  onChange: (value: string) => void
  onSubmit?: () => void
  placeholder?: string
  rows?: number
  /** Uploads a pasted/dropped file and resolves with the markdown to insert. */
  onUploadFile?: (file: File) => Promise<string | null>
  /** Maps attachment ids to signed URLs so Preview can draw inline images. */
  previewUrls?: Map<number, string>
  disabled?: boolean
  autoFocus?: boolean
}

type ToolbarAction = {
  icon: typeof Bold
  label: string
  shortcut?: string
  apply: (sel: Selection) => { text: string; start: number; end: number }
}

type Selection = { value: string; start: number; end: number }

/** Wraps the selection, or drops in a placeholder when there isn't one. */
function wrap(sel: Selection, before: string, after: string, placeholder: string) {
  const selected = sel.value.slice(sel.start, sel.end) || placeholder
  const text = sel.value.slice(0, sel.start) + before + selected + after + sel.value.slice(sel.end)
  return { text, start: sel.start + before.length, end: sel.start + before.length + selected.length }
}

/** Prefixes every line the selection touches — for lists and quotes. */
function prefixLines(sel: Selection, prefix: (index: number) => string) {
  const lineStart = sel.value.lastIndexOf('\n', sel.start - 1) + 1
  const lineEnd = sel.value.indexOf('\n', sel.end)
  const stop = lineEnd === -1 ? sel.value.length : lineEnd
  const block = sel.value.slice(lineStart, stop) || ''
  const prefixed = block.split('\n').map((line, i) => prefix(i) + line).join('\n')
  const text = sel.value.slice(0, lineStart) + prefixed + sel.value.slice(stop)
  return { text, start: lineStart, end: lineStart + prefixed.length }
}

const ACTIONS: ToolbarAction[] = [
  { icon: Bold, label: 'Bold', shortcut: '⌘B', apply: s => wrap(s, '**', '**', 'bold text') },
  { icon: Italic, label: 'Italic', shortcut: '⌘I', apply: s => wrap(s, '_', '_', 'italic text') },
  { icon: Code, label: 'Code', apply: s => wrap(s, '`', '`', 'code') },
  { icon: Link2, label: 'Link', shortcut: '⌘K', apply: s => wrap(s, '[', '](https://)', 'link text') },
  { icon: List, label: 'Bulleted list', apply: s => prefixLines(s, () => '- ') },
  { icon: ListOrdered, label: 'Numbered list', apply: s => prefixLines(s, i => `${i + 1}. `) },
  { icon: Quote, label: 'Quote', apply: s => prefixLines(s, () => '> ') },
]

export function MarkdownComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  rows = 3,
  onUploadFile,
  previewUrls,
  disabled = false,
  autoFocus = false,
}: MarkdownComposerProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(0)

  const run = useCallback((action: ToolbarAction) => {
    const el = ref.current
    if (!el) return
    const next = action.apply({ value, start: el.selectionStart, end: el.selectionEnd })
    onChange(next.text)
    // React re-renders before the selection can be restored, so defer it.
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(next.start, next.end)
    })
  }, [value, onChange])

  const insertAtCursor = useCallback((snippet: string) => {
    const el = ref.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const text = value.slice(0, start) + snippet + value.slice(end)
    onChange(text)
    requestAnimationFrame(() => {
      el?.focus()
      const at = start + snippet.length
      el?.setSelectionRange(at, at)
    })
  }, [value, onChange])

  const upload = useCallback(async (files: File[]) => {
    if (!onUploadFile || files.length === 0) return
    setUploading(n => n + files.length)
    try {
      for (const file of files) {
        const snippet = await onUploadFile(file)
        if (snippet) insertAtCursor(snippet)
      }
    } finally {
      setUploading(0)
    }
  }, [onUploadFile, insertAtCursor])

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    if (!onUploadFile) return
    const files = Array.from(e.clipboardData.files)
    if (files.length === 0) return
    e.preventDefault()
    void upload(files)
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (!onUploadFile) return
    void upload(Array.from(e.dataTransfer.files))
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    const mod = e.metaKey || e.ctrlKey
    if (!mod) return
    if (e.key === 'Enter') {
      e.preventDefault()
      onSubmit?.()
      return
    }
    const byKey: Record<string, string> = { b: 'Bold', i: 'Italic', k: 'Link' }
    const label = byKey[e.key.toLowerCase()]
    if (label) {
      e.preventDefault()
      const action = ACTIONS.find(a => a.label === label)
      if (action) run(action)
    }
  }

  return (
    <div
      className={cn(
        'rounded-md border bg-bg-elevated transition-colors',
        dragging ? 'border-accent ring-2 ring-accent/20' : 'border-border',
      )}
      onDragOver={e => { if (onUploadFile) { e.preventDefault(); setDragging(true) } }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        {ACTIONS.map(action => (
          <button
            key={action.label}
            type="button"
            title={action.shortcut ? `${action.label} (${action.shortcut})` : action.label}
            aria-label={action.label}
            disabled={disabled || tab === 'preview'}
            onClick={() => run(action)}
            className="rounded p-1.5 text-fg-subtle hover:bg-bg-muted hover:text-fg disabled:opacity-40"
          >
            <action.icon size={14} />
          </button>
        ))}

        {onUploadFile && (
          <>
            <span className="mx-1 h-4 w-px bg-border" />
            <button
              type="button"
              title="Attach an image or file"
              aria-label="Attach"
              disabled={disabled || tab === 'preview'}
              onClick={() => fileRef.current?.click()}
              className="rounded p-1.5 text-fg-subtle hover:bg-bg-muted hover:text-fg disabled:opacity-40"
            >
              <ImagePlus size={14} />
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={e => {
                void upload(Array.from(e.target.files ?? []))
                e.target.value = ''
              }}
            />
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          {uploading > 0 && <span className="text-[11px] text-fg-subtle">Uploading…</span>}
          {(['write', 'preview'] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded px-2 py-1 text-[11px] font-medium capitalize',
                tab === t ? 'bg-bg-muted text-fg' : 'text-fg-subtle hover:text-fg',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === 'write' ? (
        <textarea
          ref={ref}
          value={value}
          rows={rows}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="w-full resize-y bg-transparent px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none"
        />
      ) : (
        <div className="min-h-[5rem] px-3 py-2">
          {value.trim()
            ? <div className="prose-comment text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(value, previewUrls) }} />
            : <p className="text-sm text-fg-subtle">Nothing to preview yet.</p>}
        </div>
      )}
    </div>
  )
}
